import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Icon from 'components/Icon'
import cn from 'classnames'
import './Link.scss'

export const LINK_TYPES = {
    regular: 'regular',
    external: 'external',
    restricted: 'restricted',
    download: 'download'
}

export const LINK_THEMES = {
    light: 'light',
    lightTotal: 'light-total',
    dark: 'dark',
}

export const LINK_SIZES = {
    small: 'small',
    normal: 'normal',
    large: 'large'
}

export const LINK_ACTION_TYPES = {
    expand: 'expand',
    toggleUp: 'toggle-up',
    toggleDown: 'toggle-down',
    back: 'back',
}

export const LINK_ACTION_ICON_POSITIONS = {
    left: 'left',
    right: 'right',
}

export const LINK_BEHAVIORS = {
    text: 'text',
    card: 'card'
}

export const mapLinkTypeToIconName = (linkType) => {
    switch (linkType) {
        case LINK_TYPES.external:
            return 'external';
        case LINK_TYPES.restricted:
            return 'lock';
        case LINK_TYPES.download:
            return 'download-outline';
        case LINK_TYPES.regular:
        default:
            return 'arrow-right';
    }
}

export const mapActionTypeToIconName = (actionType) => {
    switch (actionType) {
        case LINK_ACTION_TYPES.expand:
            return 'expand';
        case LINK_ACTION_TYPES.toggleUp:
            return 'caret-thin-up';
        case LINK_ACTION_TYPES.toggleDown:
            return 'caret-thin-down';
        case LINK_ACTION_TYPES.back:
            return 'arrow-left';
        default:
            return null;
    }
}

const Link = ({ 
    className, 
    openInNewTab, 
    url, 
    text, 
    ariaLabel,
    action,
    actionIconPosition = LINK_ACTION_ICON_POSITIONS.left,
    theme = LINK_THEMES.dark,
    size = LINK_SIZES.normal,
    additionalAttributes = {},
    behavior,
    type = LINK_TYPES.regular,
    children,
    onClick
}) => {
    const linkIcon = useMemo(() => mapLinkTypeToIconName(type), [type]);
    const actionIcon = useMemo(() => mapActionTypeToIconName(action), [action]);
    const RootComponent = url ? 
        'a' : 
        (onClick ? 'button' : 'span')

    const hideIconBasedOnBehavior = (behavior === LINK_BEHAVIORS.card && type === LINK_TYPES.regular) || behavior === LINK_BEHAVIORS.text;

    const showActionIcon = !hideIconBasedOnBehavior && actionIcon;
    const showTypeIcon = !hideIconBasedOnBehavior && linkIcon && !action;

    return (
        <RootComponent
            href={url}
            aria-label={ariaLabel}
            className={cn(
                'link',
                `link_theme-${theme}`,
                `link_size-${size}`,
                className,
                {
                    [`link_behavior-${behavior}`]: behavior,
                    [`link_with-action`]: action
                }
            )}
            target={openInNewTab ? '_blank' : null}
            rel={openInNewTab ? 'noopener noreferrer' : null}
            {...(onClick && typeof onClick === 'function' && { onClick })}
            {...additionalAttributes}
        >
            {children ? children : (
                <React.Fragment>
                    {showActionIcon && actionIconPosition === LINK_ACTION_ICON_POSITIONS.left && (
                        <Icon use={actionIcon} className="link__icon link__icon_left link__icon_action" />
                    )}
                    <span className="link__text">{text}</span>
                    {showActionIcon && actionIconPosition === LINK_ACTION_ICON_POSITIONS.right && (
                        <Icon use={actionIcon} className="link__icon link__icon_right link__icon_action" />
                    )}
                    {showTypeIcon && <Icon use={linkIcon} className="link__icon link__icon_right" />}
                </React.Fragment>
            )}
        </RootComponent>
    )
}

Link.propTypes = {
    className: PropTypes.string,
    openInNewTab: PropTypes.bool,
    url: PropTypes.bool,
    text: PropTypes.string,
    ariaLabel: PropTypes.string,
    additionalAttributes: PropTypes.object,
    behavior: PropTypes.oneOf(Object.values(LINK_BEHAVIORS)),
    type: PropTypes.oneOf(Object.values(LINK_TYPES)),
    theme: PropTypes.oneOf(Object.values(LINK_THEMES)),
    size: PropTypes.oneOf(Object.values(LINK_SIZES)),
    action: PropTypes.oneOf(Object.values(LINK_ACTION_TYPES)),
    actionIconPosition: PropTypes.oneOf(Object.values(LINK_ACTION_ICON_POSITIONS)),
    children: PropTypes.node,
    onClick: PropTypes.func
}

Link.defaultProps = {
    openInNewTab: false
}

Link.propTypesMeta = {
    theme: 'exclude',
    additionalAttributes: 'exclude',
    action: 'exclude',
    actionIconPosition: 'exclude',
    onClick: 'exclude'
}

export default Link
